/**
 * The settings screen.
 *
 * A full-screen overlay with a section rail down the left, matching the shape
 * the user asked for: Profile, Audio, Gameplay, Payment History, Withdrawal.
 * On a phone the rail becomes a scrolling strip along the top rather than a
 * menu behind a tap, for the same reason the landing page's section rail does.
 *
 * Two kinds of section live here and they behave differently on purpose:
 *
 * - **Preferences** (Audio, Gameplay) apply the instant you move the control.
 *   There is no Save button, because a volume you cannot hear until you commit
 *   it is not a volume control. `settings-store.ts` debounces the write.
 * - **Account and money** (Profile, Payment History, Withdrawal) are explicit.
 *   Changing an email or asking for a payout on the way past a slider would be
 *   a bad surprise.
 */

import { api, ApiError } from '../../../core/network/api';
import { authStore } from '../../../core/state/auth-store';
import { adoptLocaleSetting, getIntlLocale, localePersisted, N_, RELANGUAGE_EVENT, rememberLocaleSetting, t } from '../../i18n/i18n';
import { serverText } from '../../config/served-text';
import { esc } from '../item-icon';
import { iconMarkup } from '../icon';
import {
  audioSection,
  gameplaySection,
  paymentsSection,
  percent,
  invoicesSection,
  profileSection,
  railMarkup,
  setDisplayLocale,
  skeleton,
  withdrawalSection,
  type SectionId,
} from './settings-sections';
import { resetTutorial } from '../../net/tutorial-api';
import { settingsStore } from './settings-store';
import {
  MOUSE_SENSITIVITY_MAX,
  MOUSE_SENSITIVITY_MIN,
  type AudioSettings,
  type InvoiceRow,
  type PaymentEntry,
  type SettingsOverview,
  type WithdrawalOverview,
} from './settings-types';

/**
 * The message the API actually sent, when it sent one.
 *
 * Nest's validation pipe returns `message` as an array of strings, so "That
 * name is already taken" and `["username must be longer than 3 characters"]`
 * both have to end up as one readable line. Anything else falls back to the
 * caller's sentence rather than surfacing "Request failed with status code 400".
 *
 * The server writes English. Whatever it says is passed through `t()`, so a
 * message that also exists as a key (the client-side checks below, the
 * fallbacks) comes out translated; anything else reads in English. The
 * `fallback` arrives already translated.
 */
/**
 * The sentences `backend/src/game/settings/settings.service.ts` can answer
 * with, marked so the catalogues carry them and `t(message)` finds them.
 * Kept in step by hand; a new one simply reads in English until it is added.
 */
export const KNOWN_SERVER_MESSAGES: readonly string[] = [
  N_('That name is already taken'),
  N_('Current password is incorrect'),
  N_('The new password must differ from the current one'),
  N_('Password is incorrect'),
  N_('That is already your address'),
  N_('That address is already in use'),
  N_('Invalid or expired link'),
  N_('Already verified'),
  N_('A submission is already under review'),
  N_('Invalid date of birth'),
  N_('You must be at least 18'),
  N_('User not found'),
  N_('A username may use only letters, digits, _ and -'),
  N_('Choose a country from the list'),
];

const AXIOS_NOISE = /^Request failed|^Network Error$|^timeout of \d+ms|^Failed to fetch$/i;

export function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const detail = (err.details ?? {}) as { message?: unknown };
    const m = detail.message;
    if (typeof m === 'string' && m.trim()) return serverText(m);
    if (Array.isArray(m) && typeof m[0] === 'string') return serverText(m[0]);
    // axios's own words ("Request failed with status code 400", "Network
    // Error", "timeout of 15000ms exceeded") are not sentences for a player.
    if (err.message && !AXIOS_NOISE.test(err.message)) return serverText(err.message);
    return fallback;
  }
  if (err instanceof Error && err.message && !AXIOS_NOISE.test(err.message)) return serverText(err.message);
  return fallback;
}

let openOverlay: HTMLDivElement | null = null;

/**
 * The way into /admin-panel (2026-10-14). The route existed but nothing in the
 * game linked to it, so an admin had to know the URL. Shown to ADMIN only —
 * the route and every /api/admin call check the role again, this just hides
 * a door that would bounce everyone else back to /play.
 */
export function adminLinkMarkup(role: string | undefined): string {
  if (role !== 'ADMIN') return '';
  return `<a id="set-admin" href="/admin-panel" class="btn btn-quiet shrink-0 min-h-11">${esc(t('Admin panel'))}</a>`;
}

export function openSettings(initial: SectionId = 'profile'): void {
  // One at a time. The header button is easy to double-tap.
  if (openOverlay) return;

  const overlay = document.createElement('div');
  openOverlay = overlay;
  overlay.className = 'fixed inset-0 z-50 bg-lobby flex flex-col';
  overlay.innerHTML = `
    <header class="shrink-0 flex items-center gap-3 px-3 sm:px-6 py-3 border-b border-border/60 bg-bg/70">
      <h2 class="font-heading text-gold text-lg sm:text-2xl tracking-wide mr-auto">${esc(t('Settings'))}</h2>
      ${adminLinkMarkup(authStore.get().user?.role)}
      <button id="set-close" type="button" aria-label="${esc(t('Close settings'))}"
        class="btn btn-quiet min-w-11 px-0 justify-center shrink-0">${iconMarkup('close')}</button>
    </header>

    <div class="flex-1 min-h-0 grid grid-cols-1 sm:grid-cols-[minmax(0,13rem)_minmax(0,1fr)]">
      <nav id="set-rail" role="tablist" aria-orientation="vertical" aria-label="${esc(t('Settings sections'))}"
        class="settings-rail">${railMarkup(initial)}</nav>
      <div id="set-panel" role="tabpanel" tabindex="-1"
        class="min-h-0 overflow-y-auto px-3 sm:px-6 py-4 sm:py-6"></div>
    </div>
  `;
  document.body.appendChild(overlay);

  const $ = <T extends HTMLElement>(id: string) => overlay.querySelector(`#${id}`) as T;
  const railEl = $<HTMLElement>('set-rail');
  const panelEl = $<HTMLDivElement>('set-panel');
  const closeBtn = $<HTMLButtonElement>('set-close');

  let section: SectionId = initial;
  let overview: SettingsOverview | null = null;
  let payments: PaymentEntry[] | null = null;
  let invoices: InvoiceRow[] | null = null;
  let withdrawals: WithdrawalOverview | null = null;

  // ─── Rendering ─────────────────────────────────────────────────────────────

  function render(): void {
    panelEl.setAttribute('aria-labelledby', `set-tab-${section}`);
    if (overview) {
      // Redirect off a hidden tab before drawing the rail, so the rail marks
      // the tab actually shown (2026-10-19: it marked none after a redirect).
      if ((section === 'withdrawal' && !overview.features.withdrawals)
        || (section === 'invoices' && !overview.features.invoices)) {
        section = 'profile';
        panelEl.setAttribute('aria-labelledby', `set-tab-${section}`);
      }
      railEl.innerHTML = railMarkup(section, overview.features);
    }
    if (!overview) {
      panelEl.innerHTML = skeleton(t('Loading your settings…'));
      return;
    }
    const prefs = settingsStore.get();
    switch (section) {
      case 'profile':
        panelEl.innerHTML = profileSection(overview);
        wireProfile();
        break;
      case 'audio':
        panelEl.innerHTML = audioSection(prefs.audio);
        wirePreferences();
        break;
      case 'gameplay':
        panelEl.innerHTML = gameplaySection(prefs.gameplay);
        wirePreferences();
        break;
      case 'payments':
        panelEl.innerHTML = paymentsSection(payments, overview.features.invoices);
        break;
      case 'invoices':
        panelEl.innerHTML = invoicesSection(invoices, overview.features.invoices);
        break;
      case 'withdrawal':
        panelEl.innerHTML = withdrawalSection(withdrawals);
        wireWithdrawal();
        break;
    }
  }

  function selectSection(next: SectionId, focus: boolean): void {
    section = next;
    railEl.querySelectorAll<HTMLButtonElement>('[data-section]').forEach((b) => {
      const on = b.dataset.section === next;
      b.setAttribute('aria-selected', String(on));
      b.tabIndex = on ? 0 : -1;
      if (!on) return;
      if (focus) b.focus();
      // On a phone the rail is a sideways strip, so arrowing to Withdrawal can
      // land on an item that is off screen.
      b.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
    render();
    // A new section starts at its top, not wherever the last one was scrolled to.
    panelEl.scrollTop = 0;
    // These three are the only sections with their own request, and all are
    // cheap enough to fetch on first view rather than on open.
    if (next === 'payments' && payments === null) void loadPayments();
    if (next === 'invoices' && invoices === null) void loadInvoices();
    if (next === 'withdrawal' && withdrawals === null) void loadWithdrawals();
  }

  // ─── Wiring ────────────────────────────────────────────────────────────────

  function say(id: string, text: string, tone: 'ok' | 'bad' = 'ok'): void {
    const el = overlay.querySelector<HTMLElement>(`#${id}`);
    if (!el) return;
    el.textContent = text;
    el.className = `text-[13px] ${tone === 'bad' ? 'text-error' : 'text-success'}`;
  }

  async function submit(
    btnId: string,
    msgId: string,
    fallback: string,
    run: () => Promise<string>,
  ): Promise<void> {
    const btn = overlay.querySelector<HTMLButtonElement>(`#${btnId}`);
    if (!btn || btn.disabled) return;
    btn.disabled = true;
    try {
      say(msgId, await run());
    } catch (err) {
      say(msgId, errorMessage(err, fallback), 'bad');
    } finally {
      btn.disabled = false;
    }
  }

  function wireProfile(): void {
    overlay.querySelector('#set-profile-save')?.addEventListener('click', () => {
      void submit('set-profile-save', 'set-profile-msg', t('Could not save'), async () => {
        const username = $<HTMLInputElement>('set-username').value.trim();
        const locale = $<HTMLSelectElement>('set-locale').value;
        const currency = $<HTMLSelectElement>('set-currency').value;
        await api.put('/settings/profile', { username, locale, currency });
        if (overview) {
          overview.profile.username = username || null;
          overview.profile.locale = locale || null;
          overview.profile.currency = currency || null;
        }
        // In force at once: the payment and invoice tabs render their dates
        // through it the next time they are opened.
        setDisplayLocale(locale);
        // A new interface language: reload so every screen, including ones
        // already drawn behind this overlay, comes back in it.
        // With storage blocked a reload would boot in the browser language, so
        // the session redraws itself in memory instead (session-bootstrap.ts).
        if (await adoptLocaleSetting(locale)) {
          if (localePersisted(locale)) location.reload();
          else {
            // This overlay is drawn in the old language too: close it, let the
            // hub redraw, and reopen it in the new one.
            close();
            window.dispatchEvent(new Event(RELANGUAGE_EVENT));
            openSettings('profile');
          }
        }
        return t('Saved.');
      });
    });

    overlay.querySelector('#set-billing-save')?.addEventListener('click', () => {
      void submit('set-billing-save', 'set-billing-msg', t('Could not save'), async () => {
        const firstName = $<HTMLInputElement>('set-fname').value.trim();
        const lastName = $<HTMLInputElement>('set-lname').value.trim();
        const billingStreet = $<HTMLInputElement>('set-billing-street').value.trim();
        const billingCity = $<HTMLInputElement>('set-billing-city').value.trim();
        const billingZip = $<HTMLInputElement>('set-billing-zip').value.trim();
        const billingCountry = $<HTMLInputElement>('set-billing-country').value.trim();
        await api.put('/settings/profile', {
          firstName, lastName, billingStreet, billingCity, billingZip, billingCountry,
        });
        if (overview) {
          overview.profile.firstName = firstName || null;
          overview.profile.lastName = lastName || null;
          overview.profile.billing = {
            street: billingStreet || null,
            city: billingCity || null,
            zip: billingZip || null,
            country: billingCountry || null,
          };
        }
        return t('Saved.');
      });
    });

    overlay.querySelector('#set-email-save')?.addEventListener('click', () => {
      void submit('set-email-save', 'set-email-msg', t('Could not send'), async () => {
        const newEmail = $<HTMLInputElement>('set-email').value.trim();
        const currentPassword = $<HTMLInputElement>('set-email-password').value;
        await api.post('/settings/email', { newEmail, currentPassword });
        $<HTMLInputElement>('set-email-password').value = '';
        // Refetch rather than patch: the pending banner is drawn from the
        // overview, and guessing at it here would be a second source of truth.
        await loadOverview();
        return t('Check {email} for the confirmation link.', { email: newEmail });
      });
    });

    overlay.querySelector('#set-pw-save')?.addEventListener('click', () => {
      void submit('set-pw-save', 'set-pw-msg', t('Could not change password'), async () => {
        await api.post('/settings/password', {
          currentPassword: $<HTMLInputElement>('set-pw-current').value,
          newPassword: $<HTMLInputElement>('set-pw-new').value,
        });
        $<HTMLInputElement>('set-pw-current').value = '';
        $<HTMLInputElement>('set-pw-new').value = '';
        return t('Changed. Other devices have been signed out.');
      });
    });

    overlay.querySelector('#set-kyc-save')?.addEventListener('click', () => {
      void submit('set-kyc-save', 'set-kyc-msg', t('Could not submit'), async () => {
        await api.post('/settings/kyc', {
          fullName: $<HTMLInputElement>('set-kyc-name').value.trim(),
          dateOfBirth: $<HTMLInputElement>('set-kyc-dob').value,
          address: $<HTMLInputElement>('set-kyc-address').value.trim(),
          country: $<HTMLInputElement>('set-kyc-country').value.trim().toUpperCase(),
        });
        await loadOverview();
        return t('Submitted for review.');
      });
    });
  }

  /**
   * Preferences apply on `input`, which fires continuously through a drag. The
   * store handles the write; this only has to keep the readout honest.
   */
  function wirePreferences(): void {
    panelEl.querySelectorAll<HTMLInputElement>('input[data-volume]').forEach((input) => {
      input.addEventListener('input', () => {
        const key = input.dataset.volume as keyof AudioSettings;
        const value = Number(input.value) / 100;
        const out = overlay.querySelector<HTMLOutputElement>(`#set-vol-${key}-out`);
        if (out) out.textContent = percent(Number(input.value));
        settingsStore.patchAudio({ [key]: value } as Partial<AudioSettings>);
      });
    });

    const sens = panelEl.querySelector<HTMLInputElement>('#set-sens');
    sens?.addEventListener('input', () => {
      const value = Math.min(
        MOUSE_SENSITIVITY_MAX,
        Math.max(MOUSE_SENSITIVITY_MIN, Number(sens.value) / 100),
      );
      const out = overlay.querySelector<HTMLOutputElement>('#set-sens-out');
      if (out) out.textContent = `${value.toLocaleString(getIntlLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}×`;
      settingsStore.patchGameplay({ mouseSensitivity: value });
    });

    /**
     * "Replay the tutorial".
     *
     * Resets the record and announces it on `window`, rather than reaching
     * into the hub: the same button is reachable from the run menu mid-delve,
     * where there is no hub to restart and the reset simply takes effect on
     * the next visit. The hub listens for the event and starts its chapter
     * again if it is the screen underneath.
     */
    const replayBtn = panelEl.querySelector<HTMLButtonElement>('#set-tutorial-replay');
    const replayNote = panelEl.querySelector<HTMLElement>('#set-tutorial-note');
    replayBtn?.addEventListener('click', () => {
      replayBtn.disabled = true;
      if (replayNote) replayNote.textContent = '';
      resetTutorial()
        .then(() => {
          window.dispatchEvent(new CustomEvent('tallow:tutorial-replay'));
          if (replayNote) replayNote.textContent = t('Ready — it starts again in the Sconce.');
        })
        .catch(() => {
          replayBtn.disabled = false;
          if (replayNote) replayNote.textContent = t('Could not reach the server. Try again.');
        });
    });

    panelEl.querySelectorAll<HTMLButtonElement>('button[data-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.toggle as string;
        const next = btn.getAttribute('aria-checked') !== 'true';
        btn.setAttribute('aria-checked', String(next));
        if (key === 'musicEnabled') settingsStore.patchAudio({ musicEnabled: next });
        else settingsStore.patchGameplay({ [key]: next } as never);
      });
    });
  }

  function wireWithdrawal(): void {
    const methodSelect = overlay.querySelector<HTMLSelectElement>('#set-wd-method');
    const detailsInput = overlay.querySelector<HTMLInputElement>('#set-wd-details');
    if (methodSelect && detailsInput && withdrawals?.methods) {
      const isCard = () => withdrawals?.methods.find((x) => x.id === methodSelect.value)?.receiver === 'cardNumber';
      const updateField = () => {
        const card = isCard();
        detailsInput.placeholder = card ? t('Card number') : t('IBAN');
        detailsInput.maxLength = card ? 19 : 34;
        detailsInput.inputMode = card ? 'numeric' : 'text';
      };
      detailsInput.addEventListener('input', () => {
        if (!isCard()) return;
        const digits = detailsInput.value.replace(/\D/g, '').slice(0, 16);
        const masked = digits.match(/.{1,4}/g)?.join(' ') ?? '';
        if (detailsInput.value !== masked) {
          const cursor = detailsInput.selectionStart ?? masked.length;
          const oldLen = detailsInput.value.length;
          detailsInput.value = masked;
          const delta = masked.length - oldLen;
          detailsInput.setSelectionRange(cursor + delta, cursor + delta);
        }
      });
      methodSelect.addEventListener('change', updateField);
      updateField();
    }
    overlay.querySelector('#set-wd-save')?.addEventListener('click', () => {
      void submit('set-wd-save', 'set-wd-msg', t('Could not request a payout'), async () => {
        const amount = Number($<HTMLInputElement>('set-wd-amount').value);
        if (!Number.isFinite(amount) || amount <= 0) throw new Error(t('Enter an amount'));
        const destination = $<HTMLInputElement>('set-wd-details').value.replace(/\s/g, '').trim();
        if (!destination) throw new Error(t('Enter the destination'));
        await api.post('/settings/withdrawals', {
          amountCents: Math.round(amount * 100),
          payoutMethod: methodSelect?.value ?? '',
          payoutDetails: { destination },
        });
        await loadWithdrawals();
        return t('Requested. You will hear back once it has been reviewed.');
      });
    });
  }

  // ─── Loading ───────────────────────────────────────────────────────────────

  async function loadOverview(): Promise<void> {
    overview = await api.get<SettingsOverview>('/settings');
    setDisplayLocale(overview.profile.locale);
    // Another device may have changed the account's language since this
    // browser last saw it; the next boot picks it up.
    rememberLocaleSetting(overview.profile.locale);
    // The server's copy wins over the local cache the moment it arrives.
    settingsStore.hydrate(overview.preferences);
    render();
  }

  async function loadPayments(): Promise<void> {
    try {
      payments = (await api.get<{ entries: PaymentEntry[] }>('/settings/payments')).entries;
    } catch {
      payments = [];
    }
    if (section === 'payments') render();
  }

  /**
   * Core owns the list (`GET /invoices`); the game owns only the PDF. An
   * empty array on failure rather than a null: "no invoices" is a state this
   * section draws well, and a permanent skeleton is not.
   */
  async function loadInvoices(): Promise<void> {
    try {
      invoices = (await api.get<{ items: InvoiceRow[] }>('/invoices')).items;
    } catch {
      invoices = [];
    }
    if (section === 'invoices') render();
  }

  async function loadWithdrawals(): Promise<void> {
    try {
      withdrawals = await api.get<WithdrawalOverview>('/settings/withdrawals');
    } catch {
      withdrawals = null;
    }
    if (section === 'withdrawal') render();
  }

  // ─── Rail navigation ───────────────────────────────────────────────────────

  railEl.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-section]');
    if (btn) selectSection(btn.dataset.section as SectionId, false);
  });

  railEl.addEventListener('keydown', (e) => {
    const dir = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1
      : e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? -1 : 0;
    if (!dir) return;
    e.preventDefault();
    const tabs = [...railEl.querySelectorAll<HTMLButtonElement>('[data-section]')];
    const at = tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
    const next = tabs[Math.min(tabs.length - 1, Math.max(0, at + dir))];
    if (next) selectSection(next.dataset.section as SectionId, true);
  });

  // ─── Teardown ──────────────────────────────────────────────────────────────

  function close(): void {
    document.removeEventListener('keydown', onKey);
    void settingsStore.flush();
    overlay.remove();
    openOverlay = null;
    if (location.pathname.startsWith('/settings')) {
      history.pushState(null, '', '/play');
    }
  }

  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') close();
  }

  closeBtn.addEventListener('click', close);
  // The link itself is routed by main.ts's same-origin click handler; the
  // overlay lives on <body>, outside the hub's teardown, so it closes itself.
  overlay.querySelector('#set-admin')?.addEventListener('click', close);
  document.addEventListener('keydown', onKey);

  render();
  closeBtn.focus();
  void loadOverview().catch(() => {
    panelEl.innerHTML = skeleton(t('Could not load your settings. Check your connection and try again.'));
  });
}

/** For the hub's teardown — a bootstrap that unmounts must not leave this up. */
export function closeSettings(): void {
  openOverlay?.remove();
  openOverlay = null;
}
